import { getErrorStatusCode } from "@crm/errors/client-handler";
import { ErrorComponent, type ErrorComponentProps, useRouter } from "@tanstack/react-router";

/**
 * 全局错误边界组件
 *
 * 捕获路由和组件渲染过程中的错误
 */
export function DefaultErrorComponent({ error }: ErrorComponentProps) {
  const router = useRouter();
  const statusCode = getErrorStatusCode(error);

  const handleRetry = () => {
    // 对于路由加载错误，invalidate 会协调 loader 重载和错误边界重置
    void router.invalidate();
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-gray-50 px-4"
      data-error-status={statusCode}
      data-testid="route-error-boundary"
    >
      <div className="w-full max-w-md">
        <div className="space-y-4 rounded-lg bg-white p-6 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <svg
                className="h-8 w-8 text-danger"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>
            <div>
              <h1 className="font-semibold text-gray-900 text-xl">出错了</h1>
              <p className="text-gray-500 text-sm">应用遇到了一个错误</p>
            </div>
          </div>

          <div className="rounded-md border border-danger bg-danger-soft p-4">
            <p className="break-words font-mono text-danger text-sm">{error.message}</p>
          </div>

          {import.meta.env.DEV && error.stack && (
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
                查看错误堆栈
              </summary>
              <pre className="mt-2 overflow-auto rounded bg-gray-100 p-3 text-xs">
                {error.stack}
              </pre>
            </details>
          )}

          <div className="flex gap-3">
            <button
              className="flex-1 rounded-md bg-info px-4 py-2 text-white transition-colors hover:bg-info"
              onClick={handleRetry}
              type="button"
            >
              重试
            </button>
            <a
              className="flex-1 rounded-md bg-gray-100 px-4 py-2 text-center text-gray-700 transition-colors hover:bg-gray-200"
              href="/"
            >
              返回首页
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 使用 TanStack Router 的默认错误组件作为后备
 */
export function FallbackErrorComponent(props: ErrorComponentProps) {
  return <ErrorComponent {...props} />;
}
